import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, LogOut } from "lucide-react";

export function Navbar({ variant = "landing" }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isLanding = variant === "landing";
  const links = isLanding
    ? [
        { label: "The Story", to: "#story" },
        { label: "What We Do", to: "#services" },
        { label: "Between Appointments", to: "#between" },
        { label: "Contact", to: "#contact" },
      ]
    : [];

  const authed = user && typeof user === "object";

  return (
    <header
      className={`sticky top-0 z-40 w-full ${
        isLanding
          ? `backdrop-blur-xl ${scrolled ? "bg-brand-navy/85" : "bg-brand-navy/60"} border-b border-white/10`
          : "bg-white/90 backdrop-blur-xl border-b border-brand-navy/10"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link">
          <Logo variant={isLanding ? "light" : "dark"} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.to}
              href={l.to}
              className={`text-sm font-medium tracking-tight ${
                isLanding ? "text-white/80 hover:text-white" : "text-brand-navy/80 hover:text-brand-navy"
              } transition-colors`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {authed ? (
            <>
              {location.pathname !== "/dashboard" && (
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-white btn-lift"
                  data-testid="nav-dashboard-btn"
                >
                  My dashboard
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className={`rounded-full ${isLanding ? "text-white hover:bg-white/10" : "text-brand-navy hover:bg-brand-navy/5"}`}
                data-testid="nav-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link">
                <Button
                  variant="ghost"
                  className={`rounded-full ${isLanding ? "text-white hover:bg-white/10" : "text-brand-navy hover:bg-brand-navy/5"}`}
                >
                  Sign in
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register-link">
                <Button className="rounded-full bg-brand-magenta hover:bg-brand-magentaDark text-white btn-lift px-5">
                  Start your journey
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className={`lg:hidden p-2 rounded-full ${isLanding ? "text-white" : "text-brand-navy"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className={`lg:hidden ${isLanding ? "bg-brand-navy border-t border-white/10" : "bg-white border-t border-brand-navy/10"} px-5 py-4 flex flex-col gap-3`}>
          {links.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={() => setOpen(false)}
              className={`py-2 text-sm font-medium ${isLanding ? "text-white/85" : "text-brand-navy/85"}`}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          <div className="flex gap-2 pt-2">
            {authed ? (
              <>
                <Button onClick={() => navigate("/dashboard")} className="flex-1 rounded-full bg-brand-teal text-white" data-testid="nav-mobile-dashboard-btn">Dashboard</Button>
                <Button variant="outline" onClick={() => { logout(); navigate("/"); }} className="flex-1 rounded-full" data-testid="nav-mobile-logout-btn">Sign out</Button>
              </>
            ) : (
              <>
                <Link to="/login" className="flex-1" onClick={() => setOpen(false)}>
                  <Button variant="outline" className="w-full rounded-full">Sign in</Button>
                </Link>
                <Link to="/register" className="flex-1" onClick={() => setOpen(false)}>
                  <Button className="w-full rounded-full bg-brand-magenta text-white">Start</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
